import * as React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Modal } from 'semantic-ui-react';

import { updateCustomPreferencesState } from '../../reducers/CustomPreferencesReducer/CustomPreferencesReducer';
import { getIsGoldDialogShown } from '../../reducers/CustomPreferencesReducer/CustomPreferencesSelectors';
import { competitionUserCompetitionResultsSelector } from '../../reducers/CompetitionsReducer/CompetitionsSelector';
import { CHALLENGE_LEADERBOARD_PATH } from '../../shared/constants';
import { shouldShowGoldDialog } from '../../shared/helpers/gold-tier-dialog';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import analytics from '../../shared/services/analytics/analytics';
import { ErrorBoundary } from '../ErrorBoundary';

import './GoldTierModal.scss';
import { useAppDispatch } from '../../shared/services/store/useAppDispatch';

export function GoldTierModal() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const competitions = useSelector(competitionUserCompetitionResultsSelector);
  const goldDialogShown = useSelector(getIsGoldDialogShown);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (shouldShowGoldDialog(competitions, goldDialogShown)) {
      // Trigger: user is in a country-level Challenge leaderboard (competition name starts with
      // "Challenge -"), per BRAINTECH-5695. New countries need no UI change.
      // Persist optimistically so the store flips `goldDialogShown` to true synchronously: a remount
      // (e.g. the App gate toggling while `getUserCompetitions` refetches) then re-reads it as true
      // and does not re-open the modal or re-fire analytics while the PATCH is still in flight.
      setIsOpen(true);
      analytics.trackCustomEvent('gold_tier_modal_shown');
      dispatch(updateCustomPreferencesState({ goldDialogShown: true }, { optimistic: true }));
    }
  }, [competitions, goldDialogShown, dispatch]);

  const handleClose = () => {
    setIsOpen(false);
    analytics.trackCustomEvent('gold_tier_modal_close');
  };

  const handleCloseIcon = () => {
    setIsOpen(false);
    analytics.trackCustomEvent('gold_tier_modal_close_icon');
  };

  const handleChallengeLeaderboard = () => {
    analytics.trackCustomEvent('gold_tier_modal_click_leaderboard_button');
    setIsOpen(false);
    history.push(CHALLENGE_LEADERBOARD_PATH);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ErrorBoundary showUI={false} isModal={true}>
      <Modal className="gold-tier-modal" open={isOpen} onClose={handleCloseIcon} closeIcon={true} closeOnDimmerClick={false} size="tiny">
        <Modal.Content>
          <div className="gold-tier-modal__title">
            <FormattedMessageHelper id="goldTier.modal.title" />
          </div>
          <div className="gold-tier-modal__subtitle">
            <FormattedMessageHelper id="goldTier.modal.subtitle" />
          </div>
          <div className="gold-tier-modal__body">
            <p>
              <FormattedMessageHelper id="goldTier.modal.body.dimensions" />
            </p>
            <p>
              <FormattedMessageHelper id="goldTier.modal.body.consultant" />
            </p>
          </div>
          <div className="gold-tier-modal__actions">
            <button className="button button--secondary" onClick={handleClose}>
              <FormattedMessageHelper id="goldTier.modal.close" />
            </button>
            <button className="button button--primary" onClick={handleChallengeLeaderboard}>
              <FormattedMessageHelper id="goldTier.modal.cta" />
            </button>
          </div>
        </Modal.Content>
      </Modal>
    </ErrorBoundary>
  );
}
